import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getTickets, type TicketListItem } from "../../../api/tickets";
import TicketFilters, { type TicketFiltersState } from "./components/TicketFilters";
import TicketTable, { type SiteMeta, type SortState, type TicketDisplay } from "./components/TicketTable";
import { getSiteSummary } from "../../../api/siteSummary";

const defaultSort: SortState = { key: "id", direction: "asc" };

export default function MaintenanceTicketsPage() {
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [siteMeta, setSiteMeta] = useState<Record<number, SiteMeta>>({});
  const [filters, setFilters] = useState<TicketFiltersState>({
    siteId: "all",
    status: "all",
    severity: "all",
    search: "",
  });
  const [sort, setSort] = useState<SortState>(defaultSort);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getTickets({});
        if (cancelled) return;
        setTickets(data);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load tickets", err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sites = useMemo(() => {
    const unique = new Map<number, string>();
    tickets.forEach((t) => {
      const metaName = siteMeta[t.site_id]?.siteName;
      unique.set(t.site_id, metaName || `Site ${t.site_id}`);
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [tickets, siteMeta]);

  const fetchSiteMeta = useCallback(
    async (siteId: number) => {
      try {
        const summary = await getSiteSummary(siteId);
        const transformers = Object.fromEntries((summary?.transformers || []).map((t: any) => [t.id, t.name]));
        const arrays = Object.fromEntries(
          (summary?.arrays || []).map((a: any) => [
            a.id,
            { name: a.array_name || a.name, transformerId: a.transformer_id ?? null },
          ]),
        );
        const inverters = Object.fromEntries((summary?.inverters || []).map((inv: any) => [inv.id, inv.name]));
        const arrayInverters = Object.fromEntries(
          (summary?.array_inverters || []).map((ai: any) => [
            ai.id,
            { name: ai.inv_array_name, arrayId: ai.array_id ?? null, inverterId: ai.inverter_id ?? null },
          ]),
        );

        setSiteMeta((prev) => ({
          ...prev,
          [siteId]: {
            siteName: summary?.site?.name,
            transformers,
            arrays,
            arrayInverters,
            inverters,
          },
        }));
      } catch (err) {
        console.error("Failed to load site summary", err);
      }
    },
    [],
  );

  useEffect(() => {
    const uniqueSiteIds = Array.from(new Set(tickets.map((t) => t.site_id)));
    const missing = uniqueSiteIds.filter((id) => !siteMeta[id]);
    if (!missing.length) return;
    missing.forEach((id) => {
      void fetchSiteMeta(id);
    });
  }, [tickets, siteMeta, fetchSiteMeta]);

  const buildDisplayInfo = useCallback(
    (ticket: TicketListItem): TicketDisplay => {
      const meta = siteMeta[ticket.site_id];
      const siteName = meta?.siteName || `Site ${ticket.site_id}`;
      const arrayMeta = ticket.array_id ? meta?.arrays?.[ticket.array_id] : undefined;
      const transformerName = arrayMeta?.name || (ticket.array_id ? `Array ${ticket.array_id}` : undefined);
      const inverterMeta = ticket.array_inverter_id ? meta?.arrayInverters?.[ticket.array_inverter_id] : undefined;
      const inverterName =
        inverterMeta?.name ||
        (inverterMeta?.inverterId && meta?.inverters?.[inverterMeta.inverterId]) ||
        (ticket.array_inverter_id ? `Inverter ${ticket.array_inverter_id}` : undefined);

      return {
        siteName,
        transformerName: transformerName || arrayMeta?.name || (ticket.array_id ? `Array ${ticket.array_id}` : undefined),
        inverterName,
      };
    },
    [siteMeta],
  );

  const filtered = useMemo(() => {
    return tickets
      .filter((t) => {
        if (filters.siteId !== "all" && t.site_id !== filters.siteId) return false;
        if (filters.status !== "all" && t.status !== filters.status) return false;
        if (filters.severity !== "all" && t.severity_opened !== filters.severity) return false;
        if (filters.search) {
          const haystack = `${t.notes ?? ""} ${t.status ?? ""}`.toLowerCase();
          if (!haystack.includes(filters.search.toLowerCase())) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const dir = sort.direction === "asc" ? 1 : -1;
        const key = sort.key;
        const displayA = buildDisplayInfo(a);
        const displayB = buildDisplayInfo(b);
        const valueForKey = (ticket: TicketListItem, display: TicketDisplay, k: SortState["key"]) => {
          switch (k) {
            case "id":
              return ticket.id ?? 0;
            case "site_id":
              return display.siteName;
            case "array_inverter_id":
              return display.inverterName || ticket.array_inverter_id || "";
            case "transformer":
              return display.transformerName || "";
            default:
              return (ticket as any)[k];
          }
        };

        const av = valueForKey(a, displayA, key);
        const bv = valueForKey(b, displayB, key);
        if (key === "opened_at") {
          return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
        }
        if (typeof av === "number" && typeof bv === "number") {
          return (av - bv) * dir;
        }
        return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
      });
  }, [tickets, filters, sort, buildDisplayInfo]);

  const displayById = useMemo(() => {
    const map: Record<number, TicketDisplay> = {};
    filtered.forEach((ticket) => {
      map[ticket.id] = buildDisplayInfo(ticket);
    });
    return map;
  }, [filtered, buildDisplayInfo]);

  return (
    <main className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Maintenance Tickets</h1>
          <p className="text-slate-600">Browse and manage maintenance tickets.</p>
        </div>
      </div>

      <TicketFilters sites={sites} value={filters} onChange={setFilters} />

      <TicketTable
        tickets={filtered}
        loading={loading}
        sort={sort}
        onSort={setSort}
        displayById={displayById}
      />
    </main>
  );
}
